import React from "react";
import "./EmbeddingRootApp.css";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { bindActionCreators } from "redux";
import { Box, Button, CircularProgress } from "@mui/material";

import { onLogout, onLogin } from "../../redux/session/actions";
import BasicToolbar from "../../generic/components/toolbar/BasicToolbar";
import { embeddingService } from "../../generic/services/EmbeddingService";
import CustomTabs from "../../generic/components/main-content/CustomTabs";
import { promptService } from "../../generic/services/PromptService";
import { dossierService } from "../../generic/services/DossierService";
import DynamicAlert from "../../generic/components/error-display/DynamicAlert";
import { FilterPanel } from "../../generic/components/filters/FilterPanel";
import { mstrPath } from "../../utils/mstrBase";

const PROMPTED = 2;
const CASHFLOW_BASE_URL =
  "http://intra.teic.techint.net/sites/reporting/DIAFI/Economics/CashFlow/CashFlow_";
const COMPACT_DOSSIER_TABS = ["pl"];
const MONTH_CODES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DEC"];

// Reference container size (CSS px) at which the base scales below were
// visually tuned: Full HD screen at 125% OS zoom (~1400x700 of usable area
// below the toolbar/tabs).
const FIT_REFERENCE_WIDTH = 1400;
const FIT_REFERENCE_HEIGHT = 700;
const FIT_BASE_SCALES = {
  default: 0.62,
  compact: 0.48,
  plHistory: 0.48 * 0.8,
  grossmg: 0.7,
};

// Base scale per dossier tab; null means natural size (Revenue).
const getDesignBaseScale = (doss) => {
  const name = String(doss?.tabName || doss?.key || "").trim().toLowerCase();
  const compactName = name.replace(/\s+/g, "");

  if (name.includes("revenue")) return null;
  if (compactName.includes("pl") && compactName.includes("history")) return FIT_BASE_SCALES.plHistory;
  if (COMPACT_DOSSIER_TABS.includes(name)) return FIT_BASE_SCALES.compact;
  if (compactName.includes("grossmg")) return FIT_BASE_SCALES.grossmg;

  return FIT_BASE_SCALES.default;
};

// The dossier is rendered by the MSTR SDK at its DESIGN size (the placeholder
// size at render time, scale 1) and then visually compressed with a CSS
// transform so it fits the tab exactly — no cropping, no inner scrollbars.
// The design size per tab is the reference area divided by the tuned scale.
// A ResizeObserver recomputes the compression factor on any resize/tab switch.
const useAutoFitScale = (baseScale) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) return undefined;

    const designW = Math.round(FIT_REFERENCE_WIDTH / baseScale);
    const designH = Math.round(FIT_REFERENCE_HEIGHT / baseScale);

    const applyFit = () => {
      const { clientWidth, clientHeight } = element;

      // Hidden containers (inactive tab / Cashflow view) report 0 — keep the
      // last computed scale until the tab becomes visible again.
      if (!clientWidth || !clientHeight) return;

      const scale = Math.min(clientWidth / designW, clientHeight / designH);

      element.style.setProperty("--mstr-fit-scale", String(scale));
      element.style.setProperty("--mstr-design-w", `${designW}px`);
      element.style.setProperty("--mstr-design-h", `${designH}px`);
    };

    applyFit();
    const resizeObserver = new ResizeObserver(applyFit);

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [baseScale]);

  return containerRef;
};

// Wrapper around each dossier container so every tab gets its own auto-fit.
const DossierContainer = ({ doss, baseScale, className, isVisible }) => {
  const containerRef = useAutoFitScale(baseScale);

  return (
    <Box
      ref={containerRef}
      className={className}
      id={"embedding-container_" + doss.id}
      sx={{ display: isVisible ? "block" : "none" }}
    />
  );
};

const getCurrentDateContext = () => {
  const now = new Date();

  return {
    currentYear: now.getFullYear(),
    currentMonthNumber: now.getMonth() + 1,
    currentMonthCode: MONTH_CODES[now.getMonth()],
  };
};

const sanitizeDateFilters = (rawFilters, options = {}) => {
  if (!rawFilters) return rawFilters;

  const { defaultCurrentYear = false } = options;
  const { currentYear, currentMonthNumber, currentMonthCode } = getCurrentDateContext();
  const filters = {};

  Object.entries(rawFilters).forEach(([key, value]) => {
    filters[key] = {
      ...value,
      elements: Array.isArray(value?.elements) ? [...value.elements] : value?.elements,
      displayedElements: Array.isArray(value?.displayedElements)
        ? [...value.displayedElements]
        : value?.displayedElements,
    };
  });

  const yearFilter = filters.Year;
  const monthFilter = filters.Month;

  if (yearFilter) {
    if (Array.isArray(yearFilter.elements)) {
      yearFilter.elements = yearFilter.elements.filter((ele) => Number(ele?.id) <= currentYear);
    }
    if (Array.isArray(yearFilter.displayedElements)) {
      yearFilter.displayedElements = yearFilter.displayedElements.filter(
        (ele) => Number(ele?.id) <= currentYear,
      );
    }

    if (defaultCurrentYear) {
      const hasCurrentYear = yearFilter.elements?.some((ele) => Number(ele?.id) === currentYear);

      if (hasCurrentYear) {
        yearFilter.value = String(currentYear);
      }
    }

    const selectedYearExists = yearFilter.elements?.some((ele) => String(ele?.id) === String(yearFilter.value));

    if (!selectedYearExists) {
      yearFilter.value = String(currentYear);
    }
  }

  if (monthFilter) {
    const selectedYear = Number(yearFilter?.value);
    const targetKey = monthFilter.displayedElements ? "displayedElements" : "elements";
    const monthElements = Array.isArray(monthFilter[targetKey]) ? [...monthFilter[targetKey]] : [];

    if (selectedYear === currentYear) {
      const allowedMonths = monthElements.filter((ele) => Number(ele?.id_int || 0) <= currentMonthNumber);
      const monthValueStillAvailable = allowedMonths.some((ele) => ele?.id === monthFilter.value);

      if (!monthValueStillAvailable) {
        const fallbackMonth =
          allowedMonths.find((ele) => ele?.id === currentMonthCode) || allowedMonths[allowedMonths.length - 1];

        monthFilter.value = fallbackMonth?.id || "";
      }
    }
  }

  return filters;
};

const ensureRegionCountryConsistency = (rawFilters, hiddenFilterValues) => {
  const filters = { ...rawFilters };

  if (!filters?.Country) {
    return filters;
  }

  const adaptedFilters = promptService.adaptPrompts(filters, hiddenFilterValues);
  const adaptedCountry = adaptedFilters?.Country;

  if (!adaptedCountry) {
    return filters;
  }

  const availableCountries = Array.isArray(adaptedCountry.displayedElements)
    ? adaptedCountry.displayedElements
    : Array.isArray(adaptedCountry.elements)
      ? adaptedCountry.elements
      : [];

  const currentCountryValue = filters.Country?.value;
  const countryIsStillValid = availableCountries.some((element) => element?.id === currentCountryValue);

  if (countryIsStillValid) {
    return filters;
  }

  const totalCountry = availableCountries.find(
    (element) =>
      String(element?.id || "")
        .trim()
        .toUpperCase() === "TOTAL" ||
      String(element?.label || "")
        .trim()
        .toUpperCase()
        .startsWith("TOD"),
  );

  const nextCountryValue = totalCountry?.id || availableCountries[0]?.id || "";

  filters.Country = {
    ...filters.Country,
    value: nextCountryValue,
  };

  return filters;
};

function EmbeddingRootApp(props) {
  const { token, config, messageBundles, groups } = props;
  const history = useHistory();
  const location = useLocation();
  const getTabId = (doss) => doss?.id || doss?.key;
  const normalizeTabName = (doss) => String(doss?.tabName || doss?.key || "").trim().toLowerCase();
  const normalizeCompactTabName = (doss) => normalizeTabName(doss).replace(/\s+/g, "");
  const isPlHistoryTab = (doss) => {
    const normalizedName = normalizeCompactTabName(doss);

    return normalizedName.includes("pl") && normalizedName.includes("history");
  };
  const isGrossMgTab = (doss) => normalizeCompactTabName(doss).includes("grossmg");
  const isRevenueTab = (doss) => normalizeTabName(doss).includes("revenue");
  const isCashflowTab = (doss) => normalizeTabName(doss).includes("cashflow");
  const embeddedDossiers = config.applications.embedding.embeddedDossier;
  const normalizeDossierParam = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, "");
  const dossierParam = normalizeDossierParam(new URLSearchParams(location.search).get("dossier"));
  const matchesDossierParam = (doss) => {
    return (
      dossierParam !== "" &&
      (normalizeDossierParam(doss?.key) === dossierParam ||
        normalizeDossierParam(doss?.tabName) === dossierParam)
    );
  };
  const initialDossier = embeddedDossiers.find(matchesDossierParam) || embeddedDossiers[0];
  //Dossier rendered - this is the embedding API instance.
  const [localDossier, setLocalDossier] = useState(null);
  //Instance, prompts and details for the rendered dossier
  const [dossierData, setDossierData] = useState(null);
  // Pre-warmed API instances (steps 1-3) for background dossiers
  const [preWarmedInstances, setPreWarmedInstances] = useState({});
  // Incremented after each filter change to re-trigger background pre-warming
  const [preWarmTrigger, setPreWarmTrigger] = useState(0);
  const [error, setError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  //Id of the current dashboard
  const [currentDash, setCurrentDash] = useState(getTabId(initialDossier));
  const allFilters = sanitizeDateFilters(
    promptService.getPromptsForDashboard(
      currentDash,
      config.applications.embedding,
      config.filterConfig.filters,
    ),
    { defaultCurrentYear: true },
  );
  //Filters to be rendered and their elements
  const [filters, setFilters] = useState(allFilters);
  // Always-current ref so the pre-warm effect can read the latest filter
  // selection without re-running on every filter state change.
  const filtersRef = useRef(filters);

  filtersRef.current = filters;

  // Live selection shown in the filter panel — the source of truth for what
  // the user actually picked, immune to background filter-state overwrites.
  const filterPanelRef = useRef(null);

  // Filters with the panel's current selection applied.
  const getFiltersWithPanelValues = (base) => {
    const panelValues = filterPanelRef.current?.getValues?.() || {};
    const merged = {};

    Object.entries(base).forEach(([key, filter]) => {
      merged[key] =
        panelValues[filter.label] !== undefined
          ? { ...filter, value: panelValues[filter.label] }
          : filter;
    });

    return merged;
  };
  //Hidden filter values due to user privileges or groups.
  const hiddenFilterValues = promptService.getHiddenFilters(
    groups,
    config.filterConfig.restrictions,
  );

  useEffect(() => {
    if (!token) {
      history.push(mstrPath("login"), { from: `${location.pathname}${location.search}` });
    } else {
      if (!isCashflowTab(initialDossier)) {
        renderDossier(initialDossier, true);
      }
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    const otherDossiers = config.applications.embedding.embeddedDossier.filter(
      (doss) => getTabId(doss) !== getTabId(initialDossier) && !isCashflowTab(doss),
    );

    const preWarmDossier = async (doss) => {
      try {
        let instance = null;
        let prompts = null;

        try {
          instance = await dossierService.createDossierInstance(
            config,
            token,
            doss.projectId,
            doss.id,
          );
        } catch (e) {
          return;
        }

        if (instance != null && instance.status === PROMPTED) {
          prompts = await promptService.populate(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
          );

          const dossierFilters = sanitizeDateFilters(
            promptService.getPromptsForDashboard(
              getTabId(doss),
              config.applications.embedding,
              config.filterConfig.filters,
            ),
            { defaultCurrentYear: true },
          );

          // Apply the user's CURRENT selection — getPromptsForDashboard only
          // carries the config defaults (Region/Country = "(Todos)"), so
          // without this the pre-warmed dossiers ignore the active filters.
          const activeFilters = filtersRef.current;

          Object.keys(dossierFilters).forEach((key) => {
            if (activeFilters?.[key]?.value !== undefined) {
              dossierFilters[key] = { ...dossierFilters[key], value: activeFilters[key].value };
            }
          });

          await promptService.answerv2(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
            prompts,
            Object.values(getFiltersWithPanelValues(dossierFilters)),
          );
        }

        setPreWarmedInstances((prev) => ({
          ...prev,
          [getTabId(doss)]: { instance, prompts },
        }));
      } catch (e) {
        // Silent fail — falls back to normal rendering on tab click
      }
    };

    Promise.all(otherDossiers.map(preWarmDossier));
  }, [token, preWarmTrigger]);

  const renderDossier = async (doss, isFirstRender) => {
    setIsRendering(true);
    const tabId = getTabId(doss);
    const preWarmed = preWarmedInstances[tabId];

    try {
      let prompts = null,
        instance = null;

      if (preWarmed) {
        // Use pre-warmed instance — skip API steps 1-3
        instance = preWarmed.instance;
        prompts = preWarmed.prompts;
      } else {
        try {
          //Create instance of the dossier
          instance = await dossierService.createDossierInstance(
            config,
            token,
            doss.projectId,
            doss.id,
          );
        } catch (e) {
          if (e.id != "INVALID_CREDENTIALS") {
            console.log(messageBundles?.GENERIC_13);
            console.log(e.message);
            setError({
              severity: "error",
              message: messageBundles?.GENERIC_13 || "No se pudo cargar el tablero.",
            });
            // Abort this dossier's render: continuing with a null instance
            // leaves a broken iframe in the container and poisons the
            // embedding state for the other tabs.

            return;
          } else {
            throw e;
          }
        }
        if (instance != null && instance.status === PROMPTED) {
          //If the dossier is prompted and the instance was generated.
          //Populate prompts with all elements and answers
          prompts = await promptService.populate(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
          );
          // Answer prompts with the selection currently shown in the panel
          // (the global `filters` state may lag behind a background render).
          await promptService.answerv2(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
            prompts,
            Object.values(getFiltersWithPanelValues(filters)),
          );
          if (isFirstRender) {
            //set filters with adapted elements due to already selected elements (in this case, the default ones)
            setFilters(promptService.adaptPrompts(filters, hiddenFilterValues));
          }
        }
      }
      //save the dossier data - project id and dossier instance
      setDossierData({
        projectId: doss.projectId,
        dossierId: doss.id,
        dossierInstanceId: instance,
        prompts: prompts,
      });
      //Render dashboard through embedding. The placeholder is temporarily
      //sized to the dossier's DESIGN size (scale 1) so the SDK lays out the
      //pages for that size; afterwards CSS compresses it into the tab.
      const placeholder = document.getElementById("embedding-container_" + doss.id);
      const designBase = getDesignBaseScale(doss);

      if (placeholder && designBase != null) {
        placeholder.classList.add("mstr-dossier-container-rendering");
        placeholder.style.setProperty("--mstr-design-w", `${Math.round(FIT_REFERENCE_WIDTH / designBase)}px`);
        placeholder.style.setProperty("--mstr-design-h", `${Math.round(FIT_REFERENCE_HEIGHT / designBase)}px`);
      }

      let dossierEmbedded = null;

      try {
        dossierEmbedded = await embeddingService.renderDossier(
          placeholder,
          config.samlLoginURL,
          config.backendServiceURL + "app/",
          doss.projectId,
          doss.id,
          localDossier,
          token,
          instance,
          "consumption",
        );
      } finally {
        if (placeholder) {
          placeholder.classList.remove("mstr-dossier-container-rendering");
        }
      }

      //Save the dossier embedded - instance returned by the embedding API.
      //This is so we can render the dashboards on tab change without rerendering.
      setLocalDossier({ ...localDossier, [doss.id]: dossierEmbedded });
    } catch (e) {
      if (e.id === "INVALID_CREDENTIALS") {
        //If the session dies in Strategy, go back to login screen, preserving
        //the destination and flagging the failure so login can detect a loop
        //(e.g. API keeps answering 401 right after a successful SAML login).
        onLogout();
        history.push(mstrPath("login"), {
          from: `${location.pathname}${location.search}`,
          authFailed: true,
        });
      } else {
        setError({
          severity: "error",
          message: e?.message || "Error inesperado al renderizar el tablero.",
        });
      }
    } finally {
      setIsRendering(false);
    }
  };
  const handleFilterChange = async (filterValues) => {
    setError(null);
    setIsRendering(true);
    // Invalidate pre-warmed instances — filters changed so cached data is stale
    setPreWarmedInstances({});
    let instance = null;
    const normalizedFilters = sanitizeDateFilters(filterValues);
    const normalizedConsistentFilters = ensureRegionCountryConsistency(
      normalizedFilters,
      hiddenFilterValues,
    );

    try {
      //Set the dashboard for reprompt.
      instance = await promptService.rePrompt(
        config,
        token,
        dossierData.projectId,
        dossierData.dossierId,
        dossierData.dossierInstanceId.mid,
      );
      //Answer prompts with the new selected elements (as shown in the panel)
      await promptService.answerv2(
        config,
        token,
        dossierData.projectId,
        dossierData.dossierId,
        instance.mid,
        dossierData.prompts,
        Object.values(normalizedConsistentFilters),
      );
      //Save the instance and new prompt answers
      setDossierData({
        projectId: dossierData.projectId,
        dossierId: dossierData.dossierId,
        dossierInstanceId: {
          ...dossierData.dossierInstanceId,
          mid: instance?.mid,
        },
        prompts: dossierData.prompts,
      });
      //Render the dashboard after filter changes
      const dossierEmbedded = await embeddingService.renderDossier(
        document.getElementById("embedding-container_" + dossierData.dossierId),
        config.samlLoginURL,
        config.backendServiceURL + "app/",
        dossierData.projectId,
        dossierData.dossierId,
        localDossier,
        token,
        { ...dossierData.dossierInstanceId, mid: instance?.mid },
        "consumption",
      );

      //Save the dossier embedded - instance returned by the embedding API.
      //This is so we can render the dashboards on tab change without rerendering.
      // Only keep the current dossier — others are stale after filter change
      setLocalDossier({ [dossierData.dossierId]: dossierEmbedded });
      //Save filters adapted.
      setFilters(promptService.adaptPrompts(normalizedConsistentFilters, hiddenFilterValues));
      // Re-trigger background pre-warming with new filter values
      setPreWarmTrigger((prev) => prev + 1);
    } catch (e) {
      setError({
        severity: "error",
        message: messageBundles?.GENERIC_14 || "No se pudieron aplicar los filtros.",
      });
    } finally {
      setIsRendering(false);
    }
  };
  const getCurrentPeriod = () => {
    const year = filters?.Year?.value || "";
    const monthSelected = filters?.Month?.value;
    const monthIdInt =
      filters?.Month?.elements?.find((month) => month.id == monthSelected)?.id_int || "";

    return `${year}${monthIdInt}`;
  };

  const openCashflow = () => {
    const period = getCurrentPeriod();

    window.open(`${CASHFLOW_BASE_URL}${period}.pptx?web=1`, "_blank", "noopener,noreferrer");
  };

  const onChangeTab = (dossierID) => {
    const tabDossier = config.applications.embedding.embeddedDossier.find(
      (x) => getTabId(x) === dossierID,
    );

    //Set current dashboard with dossier ID
    setCurrentDash(dossierID);
    if (!tabDossier || isCashflowTab(tabDossier)) return;

    if (localDossier?.[dossierID] == undefined) {
      //Render the new dashboard on tab change.
      renderDossier(tabDossier, false);
    }
  };

  const selectedTab = config.applications.embedding.embeddedDossier.find(
    (doss) => getTabId(doss) === currentDash,
  );
  const showCashflowOnly = isCashflowTab(selectedTab);

  return (
    <Box id="embedding-root">
      <BasicToolbar id="embedding-toolbar" pageName="Home / Economics / MSTR">
        <FilterPanel
          ref={filterPanelRef}
          config={config}
          currentDash={currentDash}
          filters={filters}
          handleFilterChange={handleFilterChange}
          hiddenFilterValues={hiddenFilterValues}
          setFilters={setFilters}
        />
      </BasicToolbar>
      <CustomTabs onTabChange={onChangeTab}>
        {config.applications.embedding.embeddedDossier.map((doss) => {
          return <Box key={doss.key} did={getTabId(doss)} tabName={doss.tabName} />;
        })}
      </CustomTabs>
      {showCashflowOnly && (
        <Box className="cashflow-only-container">
          <Button className="cashflow-open-button" variant="contained" onClick={openCashflow}>
            OPEN DOCUMENT IN A ANOTHER WINDOW
          </Button>
        </Box>
      )}
      {/* Keep the dossier area always mounted: unmounting it destroys the
          embedded MSTR iframes and the cached localDossier references would
          point to removed DOM nodes (dossiers "lost" after visiting Cashflow). */}
      <Box className="mstr-dossier-area" sx={{ display: showCashflowOnly ? "none" : "block" }}>
          {isRendering && !showCashflowOnly && (
            <Box className="mstr-loading-overlay">
              <CircularProgress size={36} thickness={4} />
            </Box>
          )}
          {config.applications.embedding.embeddedDossier
            .filter((doss) => !isCashflowTab(doss))
            .map((doss) => {
              const tabId = getTabId(doss);
              const isCompactTab = COMPACT_DOSSIER_TABS.includes(normalizeTabName(doss));
              const isPlHistory = isPlHistoryTab(doss);
              const isGrossMg = isGrossMgTab(doss);
              const isRevenue = isRevenueTab(doss);
              const normalizedContainerClassName = `mstr-dossier-container${isCompactTab ? " mstr-dossier-container-compact" : ""}${isPlHistory ? " mstr-dossier-container-pl-history" : ""}${isRevenue ? " mstr-dossier-container-revenue" : ""}${isGrossMg ? " mstr-dossier-container-grossmg" : ""}`;
              // Revenue keeps natural size (100%); the rest auto-fit per tab.
              const baseScale = getDesignBaseScale(doss) ?? 1;

              return (
                <DossierContainer
                  key={tabId}
                  baseScale={baseScale}
                  className={normalizedContainerClassName}
                  doss={doss}
                  isVisible={currentDash == tabId}
                />
              );
            })}
      </Box>
      {error && <DynamicAlert error={error} resetErrorBoundary={(x) => setError(null)} />}
    </Box>
  );
}
const mapStateToProps = (state) => {
  return {
    token: state.userDetails?.token,
    config: state.configuration,
    groups: state.userDetails?.groups,
  };
};
const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      onLogin: onLogin,
      onLogout: onLogout,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EmbeddingRootApp);
